import React, { FunctionComponent, ReactNode, useEffect, useRef } from 'react'
import clsx from 'clsx'

import { Text, TextProps } from '../Typography'
import { anim } from './anim'
import styles from './style.module.scss'
import { LOADER_KINDS, PULSE_KINDS } from './constants'
import { Checkmark } from './Checkmark'
import { DesignLanguage, useIsDesignLanguage, useDesignLanguagePerClass } from '../Theme'
import { Spinner } from '../Spinner'
import type { AnimationItem } from 'lottie-web'

type KindsType = typeof PULSE_KINDS
type KindsKeys = keyof KindsType
export type PulseKinds = KindsType[KindsKeys]

type LoaderKindsType = typeof LOADER_KINDS
type LoaderKindsKeys = keyof LoaderKindsType
export type LoaderKinds = LoaderKindsType[LoaderKindsKeys]

interface Props {
  text?: string
  pulse?: boolean
  inverseColor?: boolean
  testNameSpace?: string
  pulseKind?: PulseKinds
  loaderKind?: LoaderKinds
  textProps?: TextProps
  alignCenter?: boolean
  withHeader?: boolean
}

export const Loader: FunctionComponent<Props> = ({
  text = '',
  pulse = true,
  inverseColor = false,
  testNameSpace = '',
  loaderKind = LOADER_KINDS.DEFAULT,
  pulseKind = PULSE_KINDS.DEFAULT,
  textProps,
  alignCenter = false,
  withHeader = false,
}) => {
  const hasTestId = testNameSpace.length > 0
  const isDefaultLoaderKind = loaderKind === LOADER_KINDS.DEFAULT
  const isArcadeDesign = useIsDesignLanguage(DesignLanguage.Arcade)
  const designLanguageStyles = useDesignLanguagePerClass(styles)
  const componentStyle = clsx(
    styles.component,
    pulse && isDefaultLoaderKind && styles.pulseWrapper,
    alignCenter && styles.alignCenter,
    withHeader && styles.alignWithHeader,
    inverseColor && styles.inverse,
    designLanguageStyles.component
  )
  const animationContainerRef = useRef<HTMLDivElement>(null)

  const pulseStyle = clsx(styles.pulse, styles[`kind${pulseKind}`])
  const spinnerStyle = clsx(text.length > 0 && isArcadeDesign && designLanguageStyles.spinnerWithText)

  const renderComp = (): ReactNode => {
    if (isArcadeDesign) {
      return (
        <div className={spinnerStyle}>
          <Spinner />
        </div>
      )
    }

    if (loaderKind === LOADER_KINDS.CHECKMARK) {
      return <Checkmark show />
    }

    return pulse ? (
      <div data-testid='loader-pulse-inner' className={styles.pulseInner}>
        <div className={clsx(pulseStyle, styles.largePulse)}></div>
        <div className={clsx(pulseStyle, styles.mediumPulse)}></div>
        <div className={clsx(pulseStyle, styles.smallPulse)}></div>
        <div className={styles.logoWrapper} id='animation' ref={animationContainerRef} data-testid='animation'></div>
      </div>
    ) : (
      <div id='animation' ref={animationContainerRef} data-testid='animation'></div>
    )
  }

  useEffect(() => {
    let animation: AnimationItem | undefined
    const loadAnimation = async (): Promise<void> => {
      if (animationContainerRef.current === null || animationContainerRef.current.childElementCount > 0) {
        return
      }

      try {
        const lottie = (await import('lottie-web')).default // seriously 77.4k gzipped?
        animation = lottie.loadAnimation({
          container: animationContainerRef.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: anim,
        })
      } catch (error) {
        console.info('Failed to load Animation')
      }
    }

    loadAnimation()

    return () => {
      animation?.destroy()
    }
  }, [])

  return (
    <div
      className={componentStyle}
      {...(hasTestId && { 'data-testid': `${testNameSpace}-loader` })}
      aria-label={text !== '' ? text : 'loading'}
      role='alert'
      aria-busy='true'
      aria-live='assertive'
    >
      {renderComp()}
      {text.length > 0 && (
        <Text {...textProps} renderAs='div' {...(hasTestId && { testNameSpace: `${testNameSpace}-loader` })}>
          {text}
        </Text>
      )}
    </div>
  )
}
